import React, { useState } from 'react';
import { INVOICE_CSS } from '../../utils/pejmanInvoice';
import usePwaInstall from '../../hooks/usePwaInstall';

const InstallBanner = () => {
  const {
    showBanner,
    canNativeInstall,
    canIosInstall,
    justInstalled,
    dismiss,
    promptInstall
  } = usePwaInstall();
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [busy, setBusy] = useState(false);

  if (justInstalled) {
    return (
      <div
        className="mx-3 mt-3 rounded-xl px-4 py-3 text-sm"
        style={{
          background: '#ECFDF5',
          border: '1px solid #A7F3D0',
          color: '#065F46'
        }}
        role="status"
        aria-live="polite"
      >
        Pejman is installed. Open it from your home screen anytime.
      </div>
    );
  }

  if (!showBanner && !showIosHelp) return null;

  const handleInstall = async () => {
    if (canNativeInstall) {
      setBusy(true);
      await promptInstall();
      setBusy(false);
      return;
    }
    if (canIosInstall) {
      setShowIosHelp(true);
    }
  };

  return (
    <>
      {showBanner ? (
        <div
          className="pejman-install mx-0 sm:mx-3 mt-3 rounded-xl border px-3 py-3 flex items-start gap-3"
          style={{
            background: '#FFFFFF',
            borderColor: INVOICE_CSS.border,
            boxShadow: '0 4px 16px rgba(1,46,83,0.06)'
          }}
        >
          <img
            src="/images/favicon/pejman-192.png"
            alt=""
            width={40}
            height={40}
            className="h-10 w-10 rounded-lg shrink-0"
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <p
              className="text-sm font-semibold text-pretty"
              style={{ color: INVOICE_CSS.primary }}
            >
              Install Pejman
            </p>
            <p className="text-xs mt-0.5 leading-relaxed" style={{ color: INVOICE_CSS.muted }}>
              Add to your home screen for full-screen use like a native app.
            </p>
            <div className="flex flex-wrap gap-2 mt-2.5">
              <button
                type="button"
                onClick={handleInstall}
                disabled={busy}
                className="min-h-[40px] px-3.5 rounded-lg text-sm font-semibold text-white cursor-pointer disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#012E53]"
                style={{ background: INVOICE_CSS.primary }}
              >
                {busy ? 'Opening…' : canNativeInstall ? 'Install App' : 'How to Install'}
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="min-h-[40px] px-3 rounded-lg text-sm font-semibold cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-slate-300"
                style={{ color: INVOICE_CSS.muted }}
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showIosHelp ? (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
          style={{ overscrollBehavior: 'contain' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ios-install-title"
        >
          <div
            className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl p-5 pb-[max(1.5rem,env(safe-area-inset-bottom))]"
            style={{ overscrollBehavior: 'contain' }}
          >
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2
                id="ios-install-title"
                className="text-base font-semibold text-pretty"
                style={{ color: INVOICE_CSS.primary }}
              >
                Add to Home Screen
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowIosHelp(false);
                  dismiss();
                }}
                className="min-h-[40px] px-3 text-sm font-semibold"
                style={{ color: INVOICE_CSS.muted }}
                aria-label="Close install help"
              >
                Close
              </button>
            </div>
            <ol className="space-y-3 text-sm" style={{ color: INVOICE_CSS.foreground }}>
              <li className="flex gap-3">
                <span
                  className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                  style={{ background: INVOICE_CSS.primary }}
                >
                  1
                </span>
                <span>
                  Tap the <strong>Share</strong> button in Safari (square with an arrow).
                </span>
              </li>
              <li className="flex gap-3">
                <span
                  className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                  style={{ background: INVOICE_CSS.primary }}
                >
                  2
                </span>
                <span>
                  Scroll and tap <strong>Add to Home Screen</strong>.
                </span>
              </li>
              <li className="flex gap-3">
                <span
                  className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                  style={{ background: INVOICE_CSS.primary }}
                >
                  3
                </span>
                <span>
                  Tap <strong>Add</strong>. Open Pejman from your home screen for the app experience.
                </span>
              </li>
            </ol>
            <p className="text-xs mt-4" style={{ color: INVOICE_CSS.muted }}>
              Tip: Use Safari — Chrome on iPhone cannot install home-screen apps the same way.
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default InstallBanner;
